import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  Briefcase, 
  Calculator, 
  FileText, 
  ArrowRight,
  CheckCircle,
  Zap,
  FolderLock,
  BarChart3
} from 'lucide-react';

const AppSection = () => {
  const features = [
    {
      icon: Briefcase,
      title: "Missions freelance",
      description: "Postulez à des offres sélectionnées par nos partenaires ESN"
    },
    {
      icon: FolderLock,
      title: "Coffre-fort documents",
      description: "Contrats, bilans, Kbis... tous vos documents au même endroit"
    },
    {
      icon: BarChart3,
      title: "Simulation de viabilité",
      description: "Analysez si votre projet est rentable selon votre statut"
    }
  ];

  const highlights = [
    "L'application tout-en-un : fini les multiples plateformes",
    "Missions exclusives avec nos ESN partenaires",
    "Coffre-fort : contrats, bilans, Kbis, conventions",
    "Simulez gains, dépenses, charges, impôts par statut",
    "Partenaires : banque, assurance, comptable, agence web, fournisseur matériel avec remise",
    "Version Pro : facturation, comptabilité, suivi bancaire"
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div>
            
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
              L'outil <span className="text-blue-600">tout-en-un</span> des freelances
            </h2>
            
            <p className="text-lg text-gray-600 mb-8">
              Fini les multiples plateformes ! Bozzam centralise tout : recherche de missions, 
              facturation, documents, simulation de viabilité, partenaires... Une seule application pour gérer votre entreprise.
            </p>

            <ul className="space-y-3 mb-8">
              {highlights.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold"
                onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
              >
                Accéder à l'application
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Link to="/application">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 px-6 py-3 rounded-xl font-semibold w-full sm:w-auto"
                >
                  En savoir plus
                </Button>
              </Link>
            </div>
          </div>

          {/* Right content - Feature cards */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
            
            {/* ESN highlight */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-3">
                <Briefcase className="h-6 w-6" />
                <span className="font-semibold">Missions ESN</span>
              </div>
              <p className="text-blue-100">
                Accédez à des offres de missions sélectionnées et créées par nos partenaires ESN de confiance.
              </p>
            </div>

            {/* Viability highlight */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-3">
                <BarChart3 className="h-6 w-6" />
                <span className="font-semibold">Votre projet est-il viable ?</span>
              </div>
              <p className="text-emerald-100">
                Simulez vos gains, dépenses, salaire, dividendes et découvrez le statut optimal pour votre rentabilité.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppSection;
